import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowRightIcon, CheckIcon, CheckCircleIcon, SlidersHorizontalIcon, PrinterIcon, TagIcon, ChevronRightIcon, FileCheck2Icon, MessagesSquareIcon, PenLineIcon, PackageCheckIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { designGroups } from '../data/vormgeving';
import { buildBreadcrumbList, buildServiceSchema } from '../lib/structuredData';
const EASE = [0.23, 1, 0.32, 1];
const totalItems = designGroups.reduce((sum, group) => sum + group.items.length, 0);
const fadeUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-60px'
  },
  transition: {
    duration: 0.5,
    ease: EASE
  }
};
const figmaBenefits = [{
  title: 'Kleuren en teksten zelf aanpassen',
  body: 'Een adres gewijzigd of een actie erbij? Je past het aan in het document en exporteert opnieuw. Zonder wachten op een reactie.'
}, {
  title: 'Varianten staan al klaar',
  body: 'Van elk onderdeel leveren we varianten mee, bijvoorbeeld een liggende en staande versie of een lichte en donkere uitvoering.'
}, {
  title: 'Alles op één plek, ook over jaren',
  body: 'Je huisstijl ligt niet verspreid over losse bestanden op iemands laptop. Je hebt één document dat het complete verhaal bevat.'
}];
const werkwijze = [{
  title: 'Gesprek over je vak',
  body: 'We willen weten wat je doet, voor wie, en hoe je gezien wilt worden. Vaak levert dat meer op dan een lijst met voorkeuren.',
  icon: MessagesSquareIcon
}, {
  title: 'Richting kiezen',
  body: 'Je krijgt twee richtingen te zien, uitgewerkt op echte toepassingen. Geen losse logo op een witte pagina.',
  icon: PenLineIcon
}, {
  title: 'Uitwerken',
  body: 'De gekozen richting wordt doorgetrokken naar alle onderdelen die je nodig hebt, van visitekaartje tot e-mailfooter.',
  icon: FileCheck2Icon
}, {
  title: 'Opleveren in Figma',
  body: 'Je krijgt het Figma-document, de exportbestanden en een korte uitleg zodat je er zelf mee verder kunt.',
  icon: PackageCheckIcon
}];
export const VormgevingPage: React.FC = () => {
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-3034" data-mp="1apcr9ekg6lan">
      <SEO title="Digitale vormgeving en huisstijl laten maken | Noord Digital" description="Huisstijl, logo, drukwerk en digitale sjablonen voor ondernemers in Noord-Holland Noord. Opgeleverd als Figma-document dat je zelf kunt aanpassen. Prijzen op aanvraag." keywords="digitale vormgeving, huisstijl laten maken, logo laten ontwerpen, drukwerk ontwerp, visitekaartjes laten maken, huisstijlhandboek, figma huisstijl, vormgeving Noord-Holland" canonical="https://noorddigital.nl/vormgeving" jsonLd={[buildServiceSchema({
      name: 'Digitale vormgeving',
      description: 'Logo en huisstijl, drukwerk en digitale sjablonen voor ondernemers in Noord-Holland Noord, opgeleverd als aanpasbaar Figma-document.',
      path: '/vormgeving',
      serviceType: 'Grafische vormgeving en huisstijlontwerp'
    }), buildBreadcrumbList([{
      name: 'Digitale vormgeving',
      path: '/vormgeving'
    }])]} data-id="element-3035" data-mp="1fmutkm3ym29o" />
      <Header data-id="element-3036" data-mp="pdne2ib7ukjm" />

      <main data-id="element-3037" data-mp="pc2icpd60vk6">
        {/* Hero, in dezelfde huisstijl als de andere dienstpagina's */}
        <section className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-16 md:py-20" data-id="element-3038" data-mp="kfc2o359w1za">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3039" data-mp="3sv5jkj3infgv">
            <nav aria-label="Kruimelpad" className="mb-8" data-id="element-3040" data-mp="3nvj4s581hr5y">
              <ol className="flex items-center gap-1.5 text-sm text-white text-opacity-70" data-id="element-3041" data-mp="3nx4385xh5u93">
                <li data-id="element-3042" data-mp="5qpm5y3wt46h">
                  <a href="/" className="hover:text-white transition-colors duration-200" data-id="element-3043" data-mp="1nt01xf5upxs">
                    Home
                  </a>
                </li>
                <li aria-hidden="true" data-id="element-3044" data-mp="1h2yufnreiw6e">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-white text-opacity-40" data-id="element-3045" data-mp="1d2yiqavosz31" />
                </li>
                <li className="font-medium text-white" aria-current="page" data-id="element-3046" data-mp="28degb9ktrjww">
                  Digitale vormgeving
                </li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-id="element-3047" data-mp="2cgszg9428gek">
              <motion.div className="lg:col-span-7" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              ease: EASE
            }} data-id="element-3048" data-mp="2hc8db122icaz">
                <h1 className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-3049" data-mp="2vc9mr4wees22">
                  Digitale vormgeving en huisstijl voor lokale ondernemers
                </h1>
                <p className="text-white text-opacity-90 mb-8 text-lg leading-relaxed" data-id="element-3050" data-mp="3522mn8n6hiqh">
                  Je werk is in orde, maar je uitstraling loopt achter. Dat merkt een klant voordat hij je gesproken heeft. Wij zetten je merk één keer goed neer en leveren alles zo op dat je er zelf mee vooruit kunt. Van logo en huisstijl tot visitekaartjes, gevelreclame en de sjablonen die je elke week gebruikt. Vormgeving kan los, ook zonder website bij ons. Beer ontwerpt vanuit Oudkarspel voor ondernemers in{' '}
                  <a href="/werkgebied/alkmaar" className="underline underline-offset-4 decoration-white/50 hover:decoration-white transition-colors duration-200" data-id="element-3051" data-mp="tgk8ofaqvna9">Alkmaar</a>{' '}
                  en de rest van Noord-Holland Noord.
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-3052" data-mp="1h5winhdwbzdm">
                  <a href="/#contact" className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-lg group" data-id="element-3053" data-mp="1h6ekctjbv5sx">
                    Vraag een prijsopgave aan
                    <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3054" data-mp="17fjmst0msjs6" />
                  </a>
                  <a href="#figma" className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg group" data-id="element-3055" data-mp="204h6n0khqon1">
                    Wat is de Figma-oplevering?
                    <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-3056" data-mp="35ry8krryklt" />
                  </a>
                </div>
              </motion.div>

              <motion.div className="lg:col-span-5" initial={{
              opacity: 0,
              scale: 0.96
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.1,
              ease: EASE
            }} data-id="element-3057" data-mp="3kkowo09je5fs">
                <div className="bg-white rounded-2xl p-8 shadow-2xl" data-id="element-3058" data-mp="31nx93emyp45x">
                  <div className="text-center mb-6" data-id="element-3059" data-mp="31prwczlph8d6">
                    <div className="inline-flex items-center justify-center w-28 h-28 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full mb-4 p-3" data-id="element-3060" data-mp="36ntbxajxapjw">
                      <img src="https://cdn.magicpatterns.com/uploads/hr1JC9wRjcR2KJD3mjAfSG/Norden_met_kleur_waaier.png" alt="Norden met kleurwaaier, digitale vormgeving" className="w-full h-full object-contain" data-id="element-3061" data-mp="zuhd0283ux3k" />
                    </div>
                    <h2 className="font-bold text-[#111827] text-2xl mb-2" data-id="element-3062" data-mp="2kfe5o001xcq4">
                      {totalItems} onderdelen, één huisstijl
                    </h2>
                    <p className="text-[#6B7280] text-sm" data-id="element-3063" data-mp="2uciu66hddka3">
                      Van logo tot gevelreclame, alles uit dezelfde bron
                    </p>
                  </div>
                  <div className="space-y-2.5 pt-5 border-t border-gray-200" data-id="element-3064" data-mp="3860ehthvr116">
                    {designGroups.map(group => <div key={group.id} className="flex items-center justify-between gap-3" data-id="element-3065" data-mp="3376z1vj18zyd">
                        <span className="flex items-center gap-3" data-id="element-3066" data-mp="3i00oa8o3wug8">
                          <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-3067" data-mp="3d4bn60wfv5ht" />
                          <span className="text-sm text-[#111827] font-medium" data-id="element-3068" data-mp="1novfkpy5xru1">
                            {group.title}
                          </span>
                        </span>
                        <span className="text-sm text-[#6B7280]" data-id="element-3069" data-mp="wwlihethdjzi">
                          {group.items.length}
                        </span>
                      </div>)}
                    <div className="flex items-center gap-3 pt-3 mt-1 border-t border-gray-100" data-id="element-3070" data-mp="16ln1ldvbohhp">
                      <SlidersHorizontalIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-3071" data-mp="1bkq1kjwg3xit" />
                      <span className="text-sm text-[#6B7280]" data-id="element-3072" data-mp="dwxvf0fetlz3">
                        Opgeleverd in Figma, zelf aan te passen
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Wat we maken */}
        <section className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-3073" data-mp="rwz4h35tvzpn">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3074" data-mp="1zaan9ajzh8vs">
            <motion.div {...fadeUp} className="max-w-3xl mb-14" data-id="element-3075" data-mp="1zcdfm18lze3o">
              <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-4" data-id="element-3076" data-mp="23aksgkzybv9l">
                Wat we voor je maken
              </h2>
              <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-3077" data-mp="3nvr8rzph05yo">
                Je hoeft niet alles in één keer af te nemen. De meeste
                ondernemers beginnen bij hun merk en vullen de rest aan zodra
                ze het nodig hebben.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-id="element-3078" data-mp="3a0p75ornp2ro">
              {designGroups.map((group, index) => {
              const Icon = group.icon;
              return <motion.article key={group.id} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.45,
                delay: index * 0.06,
                ease: EASE
              }} className="bg-white rounded-2xl border border-gray-200 p-8 flex flex-col" data-id="element-3079" data-mp="30311i3siiauq" data-mpi={index}>
                    <div className="flex items-start justify-between gap-4 mb-5" data-id="element-3080" data-mp="2n49kuow462vm">
                      <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#EEF5FA] text-[#004AC7] flex-shrink-0" data-id="element-3081" data-mp="2i6hrrrjqgapu">
                        <Icon className="h-6 w-6" aria-hidden="true" data-id="element-3082" data-mp="2w4enymu9yf3z" />
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wide text-[#6B7280] bg-[#F7F8FA] border border-gray-200 rounded-full px-3 py-1.5" data-id="element-3083" data-mp="2r5uzofs7kr8g">
                        {group.items.length} onderdelen
                      </span>
                    </div>
                    <h3 className="font-bold text-[#111827] text-2xl mb-3" data-id="element-3084" data-mp="2d97tdmed8opv">
                      {group.title}
                    </h3>
                    <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-3085" data-mp="1krcai0nh25o1">
                      {group.intro}
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5" data-id="element-3086" data-mp="1frhks0ffnm4k">
                      {group.items.map(item => <li key={item} className="flex items-start gap-2.5 text-[#111827]" data-id="element-3087" data-mp="3rf2xq3azyl1n">
                          <CheckIcon className="h-4 w-4 text-[#004AC7] mt-1 flex-shrink-0" aria-hidden="true" data-id="element-3088" data-mp="206cvpep4ey" />
                          <span className="text-sm leading-relaxed" data-id="element-3089" data-mp="3igr9g7xlrzcd">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </motion.article>;
            })}
            </div>
          </div>
        </section>

        {/* Figma, het onderscheidende punt */}
        <section id="figma" className="w-full bg-[#1F222C] py-20 md:py-28 scroll-mt-24" data-id="element-3090" data-mp="50a1j6b4s50k">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3091" data-mp="52nu98ic954y">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-3092" data-mp="9xjp0geym6lr">
              <motion.div {...fadeUp} className="lg:col-span-5" data-id="element-3093" data-mp="2pp7ig1urlgzt">
                <p className="inline-flex items-center gap-2 text-[#7FB2FF] text-sm font-semibold mb-5" data-id="element-3094" data-mp="2upvi1bx2765n">
                  <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-3095" data-mp="2amfvh5idk3hh" />
                  Zo levert bijna niemand in de regio op
                </p>
                <h2 className="text-white font-bold text-3xl md:text-4xl lg:text-[44px] leading-tight mb-6" data-id="element-3096" data-mp="21v778nq1a4wq">
                  Je huisstijl komt als Figma-document, niet als platte pdf
                </h2>
                <p className="text-white/75 text-lg leading-relaxed mb-6" data-id="element-3097" data-mp="1rwrekgbcynl4">
                  Bij de meeste bureaus krijg je bestanden die je alleen kunt
                  bekijken. Voor elke wijziging ga je terug naar de ontwerper,
                  en dat kost tijd en geld.
                </p>
                <p className="text-white/75 leading-relaxed" data-id="element-3098" data-mp="1dx89jwdufsd1">
                  Wij leveren je huisstijl op als een werkend Figma-document.
                  Kleuren, teksten en varianten staan erin klaar en je kunt ze
                  zelf aanpassen. Je blijft eigenaar van je eigen merk, ook als
                  je ooit met iemand anders verder wilt.
                </p>
              </motion.div>

              <motion.div className="lg:col-span-7" initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.55,
              delay: 0.08,
              ease: EASE
            }} data-id="element-3099" data-mp="3ktll9qi8ec81">
                <img src="https://cdn.magicpatterns.com/patterns/generated-images/3dad8674-5068-4992-9539-32017ceda909.jpg" alt="Huisstijldocument met kleurstalen, typografie en logovarianten naast een instellingenpaneel" loading="lazy" className="w-full h-auto rounded-2xl border border-white/10 shadow-2xl" data-id="element-3100" data-mp="31uzkydujgp56" />
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14 items-stretch" data-id="element-3101" data-mp="m3bn8g7paslb">
              {figmaBenefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-40px'
            }} transition={{
              duration: 0.4,
              delay: index * 0.06,
              ease: EASE
            }} className="bg-white/5 border border-white/10 rounded-2xl p-7" data-id="element-3102" data-mp="q7b0mpxopbag" data-mpi={index}>
                  <h3 className="text-white font-semibold text-lg mb-3" data-id="element-3103" data-mp="17lud7a8brnev">
                    {benefit.title}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed" data-id="element-3104" data-mp="12lzlx5kn0vta">
                    {benefit.body}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Werkwijze */}
        <section className="w-full bg-white py-20 md:py-24" data-id="element-3105" data-mp="1qd51rkqkvo3l">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3106" data-mp="1qbk77v49k55q">
            <motion.h2 {...fadeUp} className="font-bold text-[#111827] text-3xl md:text-4xl mb-14 max-w-2xl" data-id="element-3107" data-mp="1mdmieteenl2v">
              Hoe een vormgevingstraject bij ons loopt
            </motion.h2>

            <ol className="relative border-l border-gray-200 ml-5 md:ml-6" data-id="element-3108" data-mp="29y561iw9pllc">
              {werkwijze.map((step, index) => {
              const Icon = step.icon;
              return <motion.li key={step.title} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-40px'
              }} transition={{
                duration: 0.4,
                delay: index * 0.06,
                ease: EASE
              }} className="relative pl-8 md:pl-12 pb-12 last:pb-0" data-id="element-3109" data-mp="20y8nlz0h1iyq" data-mpi={index}>
                    <span className="absolute -left-5 md:-left-6 flex items-center justify-center w-10 md:w-12 h-10 md:h-12 rounded-full bg-[#004AC7] text-white" data-id="element-3110" data-mp="13azylu19hrrz">
                      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-3111" data-mp="189jjdr44cxir" />
                    </span>
                    <p className="text-sm font-semibold text-[#004AC7] mb-1" data-id="element-3112" data-mp="3a0ti2ygj8wir">
                      Stap {index + 1}
                    </p>
                    <h3 className="font-bold text-[#111827] text-xl mb-2" data-id="element-3113" data-mp="316yfny6spk6t">
                      {step.title}
                    </h3>
                    <p className="text-[#6B7280] leading-relaxed max-w-2xl" data-id="element-3114" data-mp="2w8wvtb1jpgua">
                      {step.body}
                    </p>
                  </motion.li>;
            })}
            </ol>
          </div>
        </section>

        {/* Drukwerk via partner */}
        <section className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-3115" data-mp="3jxg2jm4y5lk9">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3116" data-mp="3jvli08xl7vgp">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-3117" data-mp="3exjyxo05vw58">
              <motion.div {...fadeUp} data-id="element-3118" data-mp="28jawmnl6b1ma">
                <img src="https://cdn.magicpatterns.com/patterns/generated-images/3585c62b-515c-4078-b280-0d4bf30f3452.jpg" alt="Gedrukte flyers, poster, menukaart en een opgerolde banner op een lichte ondergrond" loading="lazy" className="w-full h-auto rounded-2xl shadow-lg" data-id="element-3119" data-mp="28g6k92xk0zkj" />
              </motion.div>
              <motion.div {...fadeUp} data-id="element-3120" data-mp="1yc88fsczb0z7">
                <p className="inline-flex items-center gap-2 text-[#004AC7] text-sm font-semibold mb-4" data-id="element-3121" data-mp="23cw44rc5k5zr">
                  <PrinterIcon className="h-4 w-4" aria-hidden="true" data-id="element-3122" data-mp="284dzmzvepedc" />
                  Drukwerk
                </p>
                <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-5" data-id="element-3123" data-mp="2c4xzib7d6w36">
                  Het laten drukken kunnen we regelen
                </h2>
                <p className="text-[#4B5563] text-lg leading-relaxed mb-5" data-id="element-3124" data-mp="2m46pyu30tsks">
                  Wij zijn geen drukkerij. Voor het drukwerk werken we samen met
                  een externe partner die we vertrouwen. Vraag je erom, dan
                  regelen we het en houd je één aanspreekpunt.
                </p>
                <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-3125" data-mp="wo7a6u4wd4w6">
                  Het zit niet standaard in de prijs. In de opdracht staat het
                  ontwerp, en het drukken komt er alleen bij als je dat wilt. De
                  kosten van de drukker rekenen we los door, zodat je ziet wat
                  je waarvoor betaalt.
                </p>
                <p className="text-[#111827] font-medium leading-relaxed" data-id="element-3126" data-mp="38e5g057rbyl3">
                  Wil je zelf naar je eigen drukker? Ook goed. Je krijgt de
                  drukklare bestanden mee.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Kortingsregeling en prijzen */}
        <section className="w-full bg-white py-20 md:py-24" data-id="element-3127" data-mp="2oolgjldk82pq">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3128" data-mp="2om0fgyl35qt1">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch" data-id="element-3129" data-mp="2koukb259kudt">
              <motion.div {...fadeUp} className="bg-[#EEF5FA] rounded-2xl p-8 md:p-10 border border-[#D6E6F5]" data-id="element-3130" data-mp="3wjdwekf3cc2">
                <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-white text-[#004AC7] mb-5" data-id="element-3131" data-mp="3w1n5fu3jukkw">
                  <TagIcon className="h-6 w-6" aria-hidden="true" data-id="element-3132" data-mp="7iecibplgv70" />
                </span>
                <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-4" data-id="element-3133" data-mp="ccs380tbae5c">
                  Korting bij een website en hosting bij ons
                </h2>
                <p className="text-[#4B5563] leading-relaxed mb-4" data-id="element-3134" data-mp="2hqfn9eopk4v">
                  Klanten met een website en hosting bij ons krijgen korting op
                  vormgeving. De hoogte stemmen we per traject af, omdat die
                  afhangt van de omvang van het werk en van wat je al bij ons
                  hebt lopen.
                </p>
                <p className="text-[#6B7280] leading-relaxed" data-id="element-3135" data-mp="lfjikbpb5u29">
                  Dat noemen we liever eerlijk zo, dan dat we een percentage
                  roepen dat later toch niet blijkt te kloppen.
                </p>
              </motion.div>

              <motion.div {...fadeUp} className="bg-[#F7F8FA] rounded-2xl p-8 md:p-10 border border-gray-200 flex flex-col" data-id="element-3136" data-mp="1dxotrropywcy">
                <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-4" data-id="element-3137" data-mp="35jkktd8lcl5f">
                  Prijzen op aanvraag
                </h2>
                <p className="text-[#4B5563] leading-relaxed mb-4" data-id="element-3138" data-mp="2vs6plhnoa7i3">
                  Een logo voor een praktijk van twee personen is een ander
                  traject dan een complete huisstijl met gevelreclame en
                  bedrijfskleding. Daarom noemen we geen vanaf-prijs die niets
                  zegt.
                </p>
                <p className="text-[#6B7280] leading-relaxed mb-8" data-id="element-3139" data-mp="2qtn5lmsn35sd">
                  Vertel wat je nodig hebt en je krijgt een opgave met een
                  vaste prijs per onderdeel. Vormgeving kan los worden
                  afgenomen, ook als je geen website bij ons hebt.
                </p>
                <a href="/#contact" className="inline-flex items-center justify-center bg-[#004AC7] hover:bg-[#003A9E] text-white font-semibold py-4 px-8 rounded-lg transition-colors duration-200 shadow-md group mt-auto self-start" data-id="element-3140" data-mp="b1z5jiibwx5c">
                  Vraag een prijsopgave aan
                  <ArrowRightIcon className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-200" data-id="element-3141" data-mp="1vkjfv20lv0eb" />
                </a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Beer, de ontwerper */}
        <section className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-3142" data-mp="2j8jaid11fay6">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3143" data-mp="2javx53hqq9hk">
            <motion.div {...fadeUp} className="bg-white rounded-2xl border border-gray-200 p-8 md:p-12 flex flex-col md:flex-row items-center gap-10" data-id="element-3144" data-mp="2e96p7wptebxp">
              <img src="https://cdn.magicpatterns.com/uploads/3h5jPU9M1S7oTcbxfUFYhr/image.png" alt="Beer, ontwerper bij Noord Digital" loading="lazy" className="w-40 h-40 md:w-48 md:h-48 rounded-full object-cover border-4 border-[#EEF5FA] flex-shrink-0" style={{
              objectPosition: 'center 35%'
            }} data-id="element-3145" data-mp="2o7cn838i2xxa" />
              <div data-id="element-3146" data-mp="1jj1yu6lbfc9d">
                <p className="text-sm font-semibold text-[#004AC7] mb-2" data-id="element-3147" data-mp="1jjl9d28myydd">
                  Wie het maakt
                </p>
                <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-4" data-id="element-3148" data-mp="1nlf1un8ogw2g">
                  Beer is de ontwerper achter dit werk
                </h2>
                <p className="text-[#4B5563] leading-relaxed mb-6" data-id="element-3149" data-mp="1xiku3yt12t49">
                  Beer maakt de vormgeving bij Noord Digital. Hij werkt liever
                  aan iets dat jaren meegaat dan aan een ontwerp dat er nu even
                  goed uitziet. Je hebt gedurende het traject met hem direct
                  contact, niet met een tussenpersoon.
                </p>
                <a href="/over-ons/beer" className="inline-flex items-center gap-2 text-[#004AC7] font-semibold hover:text-[#003A9E] transition-colors duration-200 group" data-id="element-3150" data-mp="2g8j2kg0qmo8f">
                  Meer over Beer
                  <ArrowRightIcon className="h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-200" aria-hidden="true" data-id="element-3151" data-mp="2q7ib7yli6jvf" />
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Slot-CTA */}
        <section className="w-full bg-gradient-to-br from-[#007AFF] to-[#005FCC] py-16 md:py-20" data-id="element-3152" data-mp="4knygm8e7c42">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3153" data-mp="3vrgy3lnjf36h">
            <div className="flex flex-col lg:flex-row lg:items-center gap-8" data-id="element-3154" data-mp="3vtldp0ustqml">
              <div className="lg:flex-grow max-w-2xl" data-id="element-3155" data-mp="x8tif6rmgx2u">
                <h2 className="text-white font-bold text-3xl md:text-4xl mb-4" data-id="element-3156" data-mp="sa8i77cdedee">
                  Laat je merk een keer goed vastleggen
                </h2>
                <p className="text-white text-opacity-90 text-lg leading-relaxed" data-id="element-3157" data-mp="nhp84kcw60ec">
                  Een gesprek van een half uur is genoeg om te bepalen wat je
                  nodig hebt en wat het ongeveer kost. We komen langs in de
                  regio of bellen, wat jou beter past.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0" data-id="element-3158" data-mp="vqtl5rmx813b">
                <a href="/#contact" className="inline-flex items-center justify-center bg-white text-[#007AFF] font-semibold py-4 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-200 shadow-md" data-id="element-3159" data-mp="10qfxlyuxddb2">
                  Plan een kennismaking
                </a>
                <a href="/websites" className="inline-flex items-center justify-center border-2 border-white text-white font-semibold py-4 px-8 rounded-lg hover:bg-white/10 transition-colors duration-200" data-id="element-3160" data-mp="qsryyxu77lnm">
                  Bekijk websites
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-3161" data-mp="19quln5z9x6i9" />
    </div>;
};